<template>
  <validation-observer
    ref="observerAccount"
    v-slot="{ invalid }"
    slim>
    <oie-edit-wrapper :title="customerDetailsProxy.isCompany ? t('companyDetailsHeading') : t('customerDetailsHeading')" :contained="true" :disabled="disabled">
      <template #preview-content>
        <div class="body-smaller">
          <p>
            {{ `${tStatic('customerNumberLabel')}: ${customerDetailsProxy.customerNumber}` }}
          </p>
          <p>
            {{ `${customerDetailsProxy.isCompany ? t('companyNameLabel') : t('customerNameLabel')}: ${customerDetailsProxy.customerName}` }}
          </p>
          <p>
            {{ `${customerDetailsProxy.isCompany ? tStatic('phoneCompanyLabel') : tStatic('phoneLabel')}: ${customerDetailsProxy.customerPhone}` }}
          </p>
          <p>
            {{ `${customerDetailsProxy.isCompany ? tStatic('companyEmailLabel') : tStatic('email')}: ${customerDetailsProxy.customerEmail}` }}
          </p>
          <p>
            {{ `${t('orderConfirmationEmailCopyLabel')}: ${customerDetailsProxy.orderConfirmationEmailCopy}` }}
          </p>
          <p>
            {{ `${t('userPreferredLanguageLabel')}: ${languageTranslation}` }}
          </p>
        </div>
      </template>
      <template #edit-actions="{ toggle }">
        <oie-action-wrapper>
          <oie-base-action size="large" theme="skeleton" @click="exit(toggle)">
            {{ tStatic('genericClose') }}
          </oie-base-action>
          <oie-base-action
            size="large"
            :disabled="invalid"
            @click="save(toggle)">
            {{ tStatic('genericSave') }}
          </oie-base-action>
        </oie-action-wrapper>
      </template>
      <template #edit-content>
        <div class="input-column__container">
          <oie-base-input
            id="my-pages-customer-number"
            :value="customerDetailsProxy.customerNumber"
            :label-text="tStatic('customerNumberLabel')"
            :is-readonly="true"></oie-base-input>

          <validation-provider
            v-slot="{errors}"
            rules="required"
            slim>
            <oie-base-input
              id="my-pages-customer-name"
              v-model="customerDetailsProxy.customerName"
              :error-messages="errors"
              :label-text="`${customerDetailsProxy.isCompany ? t('companyNameLabel') : t('customerNameLabel')}*`"
              :placeholder="customerDetailsProxy.isCompany ? t('companyNamePlaceholderLabel') : t('customerNamePlaceholderLabel')"></oie-base-input>
          </validation-provider>

          <template v-if="activeCountry">
            <validation-provider
              v-if="activeCountry.useVatCode"
              v-slot="{errors}"
              :debounce="500"
              :rules="activeCountry ? `vatNumber:${activeCountry.value}` : null"
              slim>
              <oie-base-input
                id="my-pages-customer-vat"
                v-model="vatNumberProxy"
                :label-text="tStatic('vatNumberLabel')"
                :placeholder="tStatic('vatNumberPlaceholderText')"
                :error-messages="errors"
                :max-length="18"
                :prefix-value="activeCountry.value"
                :is-readonly="lockVatNumber"></oie-base-input>
            </validation-provider>

            <validation-provider v-slot="{errors}" rules="required|numeric" slim>
              <oie-base-input
                id="my-pages-customer-number"
                v-model="phoneNumberProxy"
                :error-messages="errors"
                :label-text="`${customerDetailsProxy.isCompany ? tStatic('phoneCompanyLabel') : tStatic('phoneLabel')}*`"
                :placeholder="customerDetailsProxy.isCompany ? tStatic('phoneCompanyLabel') : tStatic('phonePlaceholder')"
                :prefix-value="activeCountry.callingCode">
              </oie-base-input>
            </validation-provider>
          </template>

          <validation-provider v-slot="{errors}" rules="required" slim>
            <oie-base-input
              id="my-pages-email"
              v-model="customerDetailsProxy.customerEmail"
              :label-text="customerDetailsProxy.isCompany ? tStatic('companyEmailLabel') : tStatic('email')"
              :placeholder="customerDetailsProxy.isCompany ? tStatic('companyEmailPlaceholder') : tStatic('emailPlaceholder')"
              :error-messages="errors">
            </oie-base-input>
          </validation-provider>

          <oie-base-dropdown
            v-if="customerDetailsProxy.language"
            v-model="customerDetailsProxy.language"
            :label-text="t('userPreferredLanguageLabel')"
            :options="customerDetailsProxy.languageOptions"
            name="user-language"
            :use-filter-functionality="false"
            :default-selected="customerDetailsProxy.language">
          </oie-base-dropdown>

          <validation-provider v-slot="{errors}" rules="email" slim>
            <oie-base-input
              id="my-pages-send-order-confirmation-to"
              v-model="customerDetailsProxy.orderConfirmationEmailCopy"
              :label-text="t('sendOrderConfirmationEmailCopyToLabel')"
              :placeholder="t('sendOrderConfirmationEmailCopyToPlaceholder')"
              :error-messages="errors">
            </oie-base-input>
          </validation-provider>
        </div>
      </template>
    </oie-edit-wrapper>
  </validation-observer>
</template>

<script>
import _ from 'lodash'
import utils from '@/core/utils'

export default {
  props: {
    customerDetails: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    lockVatNumber: false
  }),
  computed: {
    activeCountry() {
      return this.clientModel.countryList.find(c => c.value === this.customerDetails.invoiceAddress.countryCode)
    },
    languageTranslation() {
      const language = _.find(this.customerDetails.languageOptions, language => language.value === this.customerDetailsProxy.language)
      return language ? language.text : ''
    },
    customerDetailsProxy: {
      get() {
        return this.customerDetails
      },
      set(val) {
        this.$emit('update:customerDetails', val)
      }
    },
    vatNumberProxy: {
      get() {
        return utils.extractVatNumberFromFormating(this.customerDetails.vatNumber, this.activeCountry.value)
      },
      set(val) {
        this.customerDetailsProxy.vatNumber = utils.saveVatNumberWithFormating(val, this.activeCountry.value)
      }
    },
    phoneNumberProxy: {
      get() {
        return utils.extractPhoneNumberFromFormating(this.customerDetails.customerPhone, this.activeCountry.callingCode)
      },
      set(val) {
        this.customerDetailsProxy.customerPhone = utils.savePhoneNumberWithFormating(val, this.activeCountry.callingCode)
      }
    }
  },
  mounted() {
    if(this.customerDetailsProxy.vatNumber) this.lockVatNumber = true
  },

  methods: {
    async save(toggle) {
      const valid = await this.$refs.observerAccount.validate()
      if (!valid) return
      this.$emit('save', toggle)
    },
    exit(toggle) {
      this.$emit('exit', toggle)
    }
  }
}
</script>
